import { Check, CircleCheck, CircleX, RotateCcw } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import type { QuizBlock } from "../../schema/index.ts";
import { pickText, type Locale } from "../../schema/core.ts";
import { useSiteCopy } from "../i18n/useSiteCopy.ts";
import { Button } from "../ui/Button.tsx";
import { cn } from "../ui/cn.ts";
import { InlineText, Paragraphs } from "./RichText.tsx";

/** Single-answer question, graded in the browser; nothing is stored. */
export const QuizBlockView = ({
  block,
  locale,
}: {
  block: QuizBlock;
  locale: Locale;
}): ReactNode => {
  const copy = useSiteCopy().blocks.quiz;
  const name = useId();
  const [selected, setSelected] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  const options = block.options.filter((option) => pickText(option.text, locale).trim());
  if (options.length === 0) return null;

  const correct = checked && selected === block.correctOptionId;
  const explanation = pickText(block.explanation, locale).trim();

  return (
    <form
      className="rounded-2xl border border-border bg-card p-5 sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (selected) setChecked(true);
      }}>
      <fieldset>
        <legend className="mb-1 text-base font-semibold tracking-wide text-primary uppercase">
          {copy.label}
        </legend>
        <p className="mb-4 font-display text-xl font-semibold">
          <InlineText text={pickText(block.question, locale)} />
        </p>
        <div className="flex flex-col gap-2.5">
          {options.map((option) => {
            const isSelected = selected === option.id;
            const isAnswer = option.id === block.correctOptionId;
            return (
              <label
                key={option.id}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-xl border border-input bg-background",
                  "px-4 py-3 has-focus-visible:outline-3 has-focus-visible:outline-offset-2",
                  "has-focus-visible:outline-ring",
                  isSelected && "border-primary bg-primary/8",
                  checked && "cursor-default",
                  checked && isSelected && !isAnswer && "border-danger bg-danger/8",
                  checked && correct && isAnswer && "border-success bg-success/10",
                )}>
                <input
                  type="radio"
                  name={name}
                  value={option.id}
                  checked={isSelected}
                  disabled={checked}
                  onChange={() => setSelected(option.id)}
                  className="mt-1.5 size-4 shrink-0 accent-primary focus-visible:outline-none"
                />
                <span className="flex-1">
                  <InlineText text={pickText(option.text, locale)} />
                </span>
                {checked && isSelected && (
                  <span className={cn("mt-1", correct ? "text-success" : "text-danger")}>
                    {correct ? (
                      <CircleCheck aria-hidden="true" className="size-5" />
                    ) : (
                      <CircleX aria-hidden="true" className="size-5" />
                    )}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </fieldset>

      <div aria-live="polite" className="mt-4">
        {checked && (
          <div
            className={cn(
              "rounded-xl px-4 py-3",
              correct ? "bg-success/10 text-foreground" : "bg-danger/8 text-foreground",
            )}>
            <p className={cn("font-semibold", correct ? "text-success" : "text-danger")}>
              {correct ? copy.correct : copy.incorrect}
            </p>
            {explanation && (
              <div className="mt-1 [&>p+p]:mt-2">
                <Paragraphs text={explanation} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4">
        {checked ? (
          !correct && (
            <Button
              variant="secondary"
              onClick={() => {
                setChecked(false);
                setSelected(null);
              }}>
              <RotateCcw aria-hidden="true" className="size-4" />
              {copy.tryAgain}
            </Button>
          )
        ) : (
          <Button type="submit" disabled={!selected}>
            <Check aria-hidden="true" className="size-4" />
            {copy.check}
          </Button>
        )}
      </div>
    </form>
  );
};
